// src/components/EmailDropdownModule.jsx
import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";

function EmailDropdownModule({ module, onChange, onDelete, isEditable }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conditionalOpen, setConditionalOpen] = useState(false);

  const handleLabelChange = (e) => {
    onChange({ ...module, label: e.target.value });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...(module.options || [])];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onChange({ ...module, options: newOptions });
  };

  const addOption = () => {
    const newOptions = [...(module.options || []), { label: "", value: "" }];
    onChange({ ...module, options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = [...(module.options || [])];
    newOptions.splice(index, 1);
    onChange({ ...module, options: newOptions });
  };

  // Toggle required flag if needed
  const handleRequiredToggle = (e) => {
    onChange({ ...module, required: e.target.checked });
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      {/* Module Header */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          EMAIL DROPDOWN MODULE
        </Typography>
        {isEditable && (
          <Box>
            <Tooltip title="Settings">
              <IconButton
                onClick={() => setSettingsOpen(!settingsOpen)}
                size="small"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Conditional Visibility">
              <IconButton
                onClick={() => setConditionalOpen(!conditionalOpen)}
                size="small"
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Module">
              <IconButton onClick={onDelete} size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Module Preview */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          padding: 2,
          backgroundColor: "#fafafa",
          marginBottom: 1,
        }}
      >
        <FormControl fullWidth variant="outlined">
          <InputLabel>{module.label || "Email Dropdown"}</InputLabel>
          <Select
            label={module.label || "Email Dropdown"}
            multiple
            value={module.multiple ? [] : ""}
            disabled
          >
            {(module.options || []).map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Settings Panel */}
      {isEditable && settingsOpen && (
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label="Module Label"
            value={module.label || ""}
            onChange={handleLabelChange}
            fullWidth
            margin="dense"
          />
          <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
            Options:
          </Typography>
          {(module.options || []).map((option, index) => (
            <Grid container spacing={1} alignItems="center" key={index}>
              <Grid item xs={5}>
                <TextField
                  label="Label"
                  value={option.label}
                  onChange={(e) => updateOption(index, "label", e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="Value"
                  value={option.value}
                  onChange={(e) => updateOption(index, "value", e.target.value)}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  onClick={() => removeOption(index)}
                  size="small"
                  disabled={(module.options || []).length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={addOption}
            sx={{ marginTop: 1 }}
          >
            Add Option
          </Button>
          <Grid container spacing={1} alignItems="center" sx={{ marginTop: 2 }}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={module.required || false}
                    onChange={handleRequiredToggle}
                    disabled={!isEditable}
                  />
                }
                label="Required"
              />
            </Grid>
          </Grid>
          {/* Additional settings (e.g., status dropdowns) can be added here if needed */}
        </Box>
      )}

      {isEditable && conditionalOpen && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body2">
            Conditional Visibility settings...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default EmailDropdownModule;
